/**
 * Widget-Editor (Route /einstellungen/widgets/:id, Umbau Drawer -> eigene Route,
 * HANDOFF-2026-08-18-UX-WIDGET-EDITOR.md). Vollseite statt Drawer (Muster: customer-detail.tsx
 * "H4: Vollseite statt Drawer") — Breadcrumb, Kopf (Name + Live/Draft-Pill + Pausieren/
 * Publizieren), 3-Zonen-Grid (StepNav | Step-Inhalt | Live-Vorschau).
 *
 * Mobile: StepNav wird zur horizontalen Pillen-Leiste (StepNav.tsx), die Aktionen im Kopf
 * wandern in ein Kebab-Menue statt gestapelter Buttons, und PreviewPane wandert NORMAL IM FLUSS
 * unter den Step-Inhalt (kein fixed Bottom-Sheet — iOS-Toolbar-Luecke bei `position:fixed`,
 * siehe globale CLAUDE.md "iOS Mobile Bottom-Nav/Toolbar-Gap"), mit einem Anker-Sprung oben.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, EmptyState, ErrorNote, Icon, LoadingRows, Rueckweg, StatusPill } from "../../ui/components";
import type { StatusMeta } from "../../ui/format";
import { useWidgetEditorForm } from "./useWidgetEditorForm";
import { StepNav, STEPS, type StepKey } from "./StepNav";
import ContentStep from "./steps/ContentStep";
import FieldsStep from "./steps/FieldsStep";
import AblaufStep from "./steps/AblaufStep";
import PrivacyStep from "./steps/PrivacyStep";
import AussehenStep from "./steps/AussehenStep";
import EmbedStep from "./steps/EmbedStep";
import PreviewPane from "./PreviewPane";

/** "Weiter" innerhalb eines Schritts (E2E: Golden Path erwartet einen "Weiter"-Knopf zwischen
 *  Schritt 1 und 2) — die Schritte bleiben trotzdem frei anwaehlbar ueber StepNav, "Weiter" ist
 *  nur eine Abkuerzung zum naechsten in der festen Reihenfolge aus STEPS. Auf dem letzten Schritt
 *  bleibt man einfach stehen. */
function naechsterSchritt(aktuell: StepKey): StepKey {
  const idx = STEPS.findIndex((s) => s.key === aktuell);
  return STEPS[idx + 1]?.key ?? aktuell;
}

const STATUS_TONE: Record<"draft" | "live" | "paused", StatusMeta["tone"]> = {
  draft: "neutral",
  live: "success",
  paused: "warn",
};
const STATUS_TEXT: Record<"draft" | "live" | "paused", string> = { draft: "Entwurf", live: "Live", paused: "Pausiert" };

export default function WidgetEditorPage() {
  const { id } = useParams<{ id: string }>();
  const editor = useWidgetEditorForm(id);
  const [step, setStep] = useState<StepKey>("content");
  const [kebabOpen, setKebabOpen] = useState(false);

  if (editor.loading) return <LoadingRows rows={6} />;
  if (editor.loadError) return <ErrorNote message={editor.loadError} />;
  if (!editor.widget) return <EmptyState icon="widgets" title="Widget nicht gefunden" />;

  const { widget } = editor;
  const isForm = widget.kind === "form";

  const AktionsKnoepfe = (
    <>
      <button
        type="button"
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 font-label-md text-label-md font-semibold text-primary hover:bg-primary/10 disabled:opacity-50 lg:w-auto lg:rounded-full lg:bg-primary lg:text-on-primary lg:hover:bg-primary/90"
        disabled={editor.actionBusy || editor.sperre !== null}
        title={editor.sperre ?? "Kopiert den aktuellen Entwurf in den Live-Stand (Version +1) und schaltet das Widget live."}
        onClick={() => {
          setKebabOpen(false);
          void editor.publish();
        }}
      >
        <Icon name="publish" className="text-[16px]" />
        {widget.publishedAt ? `Publizieren (→ Version ${widget.version + 1})` : "Publizieren (Version 1)"}
      </button>
      {widget.status === "live" && (
        <button
          type="button"
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 font-label-md text-label-md font-semibold text-error hover:bg-error/10 disabled:opacity-50 lg:w-auto"
          disabled={editor.actionBusy}
          title="Nimmt das Widget aus der öffentlichen Auslieferung, ohne etwas zu löschen."
          onClick={() => {
            setKebabOpen(false);
            void editor.pause();
          }}
        >
          <Icon name="pause_circle" className="text-[16px]" /> Pausieren
        </button>
      )}
      {widget.status === "paused" && (
        <button
          type="button"
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 font-label-md text-label-md font-semibold text-primary hover:bg-primary/10 disabled:opacity-50 lg:w-auto"
          disabled={editor.actionBusy}
          onClick={() => {
            setKebabOpen(false);
            void editor.resume();
          }}
        >
          <Icon name="play_circle" className="text-[16px]" /> Fortsetzen
        </button>
      )}
    </>
  );

  return (
    <div>
      <Rueckweg className="mb-4" eltern={{ label: "Widgets", to: "/einstellungen?tab=widgets" }} aktuell={editor.form.name || widget.name} />

      {editor.actionError && <ErrorNote message={editor.actionError} />}
      {editor.saveState === "error" && editor.saveError && <ErrorNote title="Nicht gespeichert" message={editor.saveError} />}
      {editor.sperre && (
        <p role="alert" className="mb-3 rounded-xl bg-error/10 p-3 font-body-sm text-body-sm text-error" data-testid="widget-editor-sperre">
          {editor.sperre}
        </p>
      )}

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-[25px] font-bold leading-tight tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">
            {editor.form.name || widget.name}
          </h1>
          <StatusPill meta={{ label: STATUS_TEXT[widget.status], tone: STATUS_TONE[widget.status] }} testId="widget-editor-status-pill" />
          {/* Auto-Save-Status (Bauplan: "kleiner Save-Status-Indikator") — Muster aus builder.tsx
              (`builder-save-status`/`data-save-state`): "Speichert …"/"Gespeichert" sind
              uebersetzte, layoutabhaengige Strings, das Attribut ist der robuste Selektor. */}
          <span data-testid="widget-editor-save-status" data-save-state={editor.saveState} className="flex items-center gap-1.5 font-label-md text-label-md text-on-surface-variant">
            {editor.saveState === "saving" && (
              <>
                <Icon name="sync" className="animate-spin text-[16px]" /> Speichert …
              </>
            )}
            {editor.saveState === "saved" && (
              <>
                <Icon name="cloud_done" className="text-[16px] text-secondary" /> Gespeichert
              </>
            )}
            {editor.saveState === "error" && <span className="text-error">Nicht gespeichert</span>}
          </span>
        </div>
        {/* Desktop: Buttons nebeneinander. Mobile: Kebab-Menue statt gestapelter Buttons. */}
        <div className="hidden items-center gap-3 lg:flex">{AktionsKnoepfe}</div>
        {/* ml-auto: bricht die Kopfzeile mobil um, bleibt der Knopf rechts — sonst landet er links
            und das rechtsbuendige Menue (w-64) klappt links aus dem Bild. */}
        <div className="relative ml-auto lg:hidden">
          <Button variant="ghost" className="!px-2" aria-label="Aktionen" onClick={() => setKebabOpen((o) => !o)}>
            <Icon name="more_vert" />
          </Button>
          {kebabOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setKebabOpen(false)} aria-hidden />
              <div className="absolute right-0 z-20 mt-1 w-64 space-y-1 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-2 shadow-2xl dark:border-outline/30 dark:bg-night-card">
                {AktionsKnoepfe}
              </div>
            </>
          )}
        </div>
      </div>

      <a href="#widget-editor-preview" className="mb-3 inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline lg:hidden">
        <Icon name="visibility" className="text-[16px]" /> Zur Vorschau springen
      </a>

      <div className="grid gap-6 lg:grid-cols-[200px_1fr_360px]">
        <StepNav active={step} onSelect={setStep} />

        <div className="min-w-0">
          {step === "content" && <ContentStep editor={editor} onNext={() => setStep(naechsterSchritt("content"))} />}
          {step === "fields" && isForm && <FieldsStep editor={editor} onNext={() => setStep(naechsterSchritt("fields"))} />}
          {step === "fields" && !isForm && (
            <p className="rounded-2xl border border-outline-variant/30 p-4 font-body-sm text-body-sm text-on-surface-variant dark:border-white/10">
              Bewertungs-Widgets haben keine Formularfelder.
            </p>
          )}
          {step === "ablauf" && isForm && <AblaufStep editor={editor} onNext={() => setStep(naechsterSchritt("ablauf"))} />}
          {step === "ablauf" && !isForm && (
            <p className="rounded-2xl border border-outline-variant/30 p-4 font-body-sm text-body-sm text-on-surface-variant dark:border-white/10">
              Bewertungs-Widgets haben keinen Anfrage-Ablauf.
            </p>
          )}
          {step === "privacy" && isForm && <PrivacyStep editor={editor} onNext={() => setStep(naechsterSchritt("privacy"))} />}
          {step === "privacy" && !isForm && (
            <p className="rounded-2xl border border-outline-variant/30 p-4 font-body-sm text-body-sm text-on-surface-variant dark:border-white/10">
              Bewertungs-Widgets fragen keine Gästedaten ab, daher kein Datenschutz-Link nötig.
            </p>
          )}
          {step === "aussehen" && <AussehenStep editor={editor} onNext={() => setStep(naechsterSchritt("aussehen"))} />}
          {step === "embed" && <EmbedStep editor={editor} />}
        </div>

        <div id="widget-editor-preview" className="lg:sticky lg:top-4 lg:self-start">
          <PreviewPane editor={editor} />
        </div>
      </div>
    </div>
  );
}
